import { add, set } from "date-fns";
import {
  AvailableDate,
  GetOrganisationAvailabilityDatesDocument,
  GetOrganisationAvailabilityDatesQuery,
  GetOrganisationAvailabilityDatesQueryVariables,
} from "../GeneratedGraphQL";
import {
  defaultService,
  extendedSingleDurationPaidService,
  multiDurationService,
} from "./service";

import { defaultBookingTool } from "./bookingTool";
import { defaultOrganisation } from "./organisation";
import { defaultResource } from "./resource";
import { defaultResourceGroup } from "./resourceGroup";
import { MockedResponse } from "@apollo/client/testing";

/**
 * The availability in these mocks are non deterministic.
 * Wasn't able to find a clear way of doing deterministic
 * date stories using storybook without hard coding arbitrary
 * force date props into the components being tested.
 *
 * Didn't think it was worth going down the route of having
 * to mock the JS Date functionality either.
 */

const currentMonthRanges = [
  {
    end: new Date("2124-02-12T00:00:00").toISOString(),
    start: new Date("2124-01-01T00:00:00").toISOString(),
  },
];

export const defaultAvailabilityDatesRequestVariables: GetOrganisationAvailabilityDatesQueryVariables =
  {
    bookingToolId: defaultBookingTool._id,
    duration: 30,
    id: defaultOrganisation._id,
    meta: {
      sessionId: "12345678",
    },

    ranges: currentMonthRanges,
    serviceId: defaultService._id,
    spaces: 1,
    timezone: "Europe/London",
  };

export const extendedAvailabilityDatesRequestVariables: GetOrganisationAvailabilityDatesQueryVariables =
  {
    ...defaultAvailabilityDatesRequestVariables,
    duration: extendedSingleDurationPaidService.durations[0].minutes,
    serviceId: extendedSingleDurationPaidService._id,
  };

export const genericAvailabilityDatesRequestVariables: GetOrganisationAvailabilityDatesQueryVariables =
  {
    duration: 30,
    id: defaultOrganisation._id,
    meta: {
      sessionId: "12345678",
    },

    ranges: currentMonthRanges,
    serviceId: defaultService._id,
    spaces: 1,
    timezone: "Europe/London",
  };

// Should always return availability from Monday - Friday
const firstAndMondayToFriday = [
  0, 2, 3, 4, 5, 6, 9, 10, 11, 12, 13, 16, 17, 18, 19, 20, 23, 24, 25, 26, 27,
  30,
];
export const defaultAvailabilityDates: AvailableDate[] =
  firstAndMondayToFriday.map((dayCount: number) => {
    return {
      date: add(new Date("2124-01-01T00:00:00"), {
        days: dayCount,
      }).toISOString(),
    };
  });

export const defaultAvailabilityDatesRequest: MockedResponse<GetOrganisationAvailabilityDatesQuery> =
  {
    request: {
      query: GetOrganisationAvailabilityDatesDocument,
      variables: defaultAvailabilityDatesRequestVariables,
    },
    result: {
      data: {
        organisation: {
          _id: defaultOrganisation._id,
          availabilityDates: defaultAvailabilityDates,
        },
      },
    },
  };
export const extendedAvailabilityDatesRequest: MockedResponse<GetOrganisationAvailabilityDatesQuery> =
  {
    request: {
      query: GetOrganisationAvailabilityDatesDocument,
      variables: extendedAvailabilityDatesRequestVariables,
    },
    result: {
      data: {
        organisation: {
          _id: defaultOrganisation._id,
          availabilityDates: defaultAvailabilityDates,
        },
      },
    },
  };

export const twoSpacesAvailabilityDatesRequest: MockedResponse<GetOrganisationAvailabilityDatesQuery> =
  {
    request: {
      query: GetOrganisationAvailabilityDatesDocument,
      variables: {
        ...defaultAvailabilityDatesRequestVariables,
        spaces: 2,
      },
    },
    result: {
      data: {
        organisation: {
          _id: defaultOrganisation._id,
          availabilityDates: defaultAvailabilityDates,
        },
      },
    },
  };

export const multiDurationAvailabilityDatesRequest: MockedResponse<GetOrganisationAvailabilityDatesQuery> =
  {
    request: {
      query: GetOrganisationAvailabilityDatesDocument,
      variables: {
        ...defaultAvailabilityDatesRequestVariables,
        duration: 30,
        serviceId: multiDurationService._id,
      },
    },
    result: {
      data: {
        organisation: {
          _id: defaultOrganisation._id,
          availabilityDates: defaultAvailabilityDates,
        },
      },
    },
  };

export const otherDurationAvailabilityDatesRequest: MockedResponse<GetOrganisationAvailabilityDatesQuery> =
  {
    request: {
      query: GetOrganisationAvailabilityDatesDocument,
      variables: {
        ...defaultAvailabilityDatesRequestVariables,
        duration: 60,
        serviceId: multiDurationService._id,
      },
    },
    result: {
      data: {
        organisation: {
          _id: defaultOrganisation._id,
          availabilityDates: defaultAvailabilityDates,
        },
      },
    },
  };

export const genericAvailabilityDatesRequest: MockedResponse<GetOrganisationAvailabilityDatesQuery> =
  {
    request: {
      query: GetOrganisationAvailabilityDatesDocument,
      variables: genericAvailabilityDatesRequestVariables,
    },
    result: {
      data: {
        organisation: {
          _id: defaultOrganisation._id,
          availabilityDates: defaultAvailabilityDates,
        },
      },
    },
  };

export const resourceAvailabilityDatesRequest: MockedResponse<GetOrganisationAvailabilityDatesQuery> =
  {
    request: {
      query: GetOrganisationAvailabilityDatesDocument,
      variables: {
        ...defaultAvailabilityDatesRequestVariables,
        meta: {
          sessionId: "24681012",
        },
        resourceIds: [defaultResource._id],
      },
    },
    result: {
      data: {
        organisation: {
          _id: defaultOrganisation._id,
          availabilityDates: defaultAvailabilityDates,
        },
      },
    },
  };

export const resourceGroupAvailabilityDatesRequest: MockedResponse<GetOrganisationAvailabilityDatesQuery> =
  {
    request: {
      query: GetOrganisationAvailabilityDatesDocument,
      variables: {
        ...defaultAvailabilityDatesRequestVariables,
        meta: {
          sessionId: "135790123",
        },
        resourceGroupIds: [defaultResourceGroup._id],
      },
    },
    result: {
      data: {
        organisation: {
          _id: defaultOrganisation._id,
          availabilityDates: defaultAvailabilityDates,
        },
      },
    },
  };

const previousMonthRanges = [
  {
    end: new Date("2124-01-12T00:00:00").toISOString(),
    start: new Date("2123-12-01T00:00:00").toISOString(),
  },
];

export const previousMonthAvailabilityDatesRequestVariables: GetOrganisationAvailabilityDatesQueryVariables =
  {
    bookingToolId: defaultBookingTool._id,
    duration: 30,
    id: defaultOrganisation._id,
    meta: {
      sessionId: "12345678",
    },

    ranges: previousMonthRanges,
    serviceId: defaultService._id,
    spaces: 1,
    timezone: "Europe/London",
  };

const firstFiveDays = [1, 2, 3, 4, 5];
export const previousMonthAvailabilityDates: AvailableDate[] =
  firstFiveDays.map((dayCount: number) => {
    return {
      date: add(new Date("2123-12-01T00:00:00"), {
        days: dayCount,
      }).toISOString(),
    };
  });

export const previousMonthAvailabilityDatesRequest: MockedResponse<GetOrganisationAvailabilityDatesQuery> =
  {
    request: {
      query: GetOrganisationAvailabilityDatesDocument,
      variables: previousMonthAvailabilityDatesRequestVariables,
    },
    result: {
      data: {
        organisation: {
          _id: defaultOrganisation._id,
          availabilityDates: previousMonthAvailabilityDates,
        },
      },
    },
  };

const nextMonthRanges = [
  {
    end: new Date("2124-03-14T00:00:00").toISOString(),
    start: new Date("2124-02-01T00:00:00").toISOString(),
  },
];

export const nextMonthAvailabilityDatesRequestVariables: GetOrganisationAvailabilityDatesQueryVariables =
  {
    bookingToolId: defaultBookingTool._id,
    duration: 30,
    id: defaultOrganisation._id,
    meta: {
      sessionId: "12345678",
    },

    ranges: nextMonthRanges,
    serviceId: defaultService._id,
    spaces: 1,
    timezone: "Europe/London",
  };

const fiveDays = [5, 8, 10, 12, 16];

export const nextMonthAvailabilityDates: AvailableDate[] = fiveDays.map(
  (dayCount: number) => {
    return {
      date: add(new Date("2124-02-01T00:00:00"), {
        days: dayCount,
      }).toISOString(),
    };
  },
);

export const nextMonthAvailabilityDatesRequest: MockedResponse<GetOrganisationAvailabilityDatesQuery> =
  {
    request: {
      query: GetOrganisationAvailabilityDatesDocument,
      variables: nextMonthAvailabilityDatesRequestVariables,
    },
    result: {
      data: {
        organisation: {
          _id: defaultOrganisation._id,
          availabilityDates: nextMonthAvailabilityDates,
        },
      },
    },
  };

export const noAvailabilityDatesRequest: MockedResponse<GetOrganisationAvailabilityDatesQuery> =
  {
    request: {
      query: GetOrganisationAvailabilityDatesDocument,
      variables: defaultAvailabilityDatesRequestVariables,
    },
    result: {
      data: {
        organisation: {
          _id: defaultOrganisation._id,
          availabilityDates: [],
        },
      },
    },
  };

const sixMonthRanges = [...new Array(6).keys()].map((count) => {
  const baseDate = add(new Date("2124-01-01T00:00:00"), {
    months: 1 + count,
  });

  const rangeStart = set(baseDate, {
    date: 1,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  const rangeEnd = set(
    add(baseDate, {
      months: 1,
    }),
    {
      date: 1,
      hours: 0,
      minutes: 0,
      seconds: 0,
    },
  );

  return {
    end: rangeEnd.toISOString(),
    start: rangeStart.toISOString(),
  };
});

export const futureAvailabilityDates: AvailableDate[] =
  firstAndMondayToFriday.map((dayCount: number) => {
    const baseDate = add(new Date("2124-01-01T00:00:00"), {
      months: 5,
    });

    const firstDayOfFutureMonth = set(baseDate, {
      date: 1,
      hours: 0,
      minutes: 0,
      seconds: 0,
    });

    return {
      date: add(firstDayOfFutureMonth, {
        days: dayCount,
      }).toISOString(),
    };
  });

export const futureFoundAvailabilityDatesRequest: MockedResponse<GetOrganisationAvailabilityDatesQuery> =
  {
    request: {
      query: GetOrganisationAvailabilityDatesDocument,
      variables: {
        bookingToolId: defaultBookingTool._id,
        duration: 30,
        id: defaultOrganisation._id,
        meta: {
          sessionId: "12345678",
        },
        ranges: [
          {
            end: new Date("2124-07-13T00:00:00").toISOString(),
            start: new Date("2124-06-01T00:00:00").toISOString(),
          },
        ],
        requestRangesIndividually: true,
        serviceId: defaultService._id,
        spaces: 1,
        timezone: "Europe/London",
      },
    },
    result: {
      data: {
        organisation: {
          _id: defaultOrganisation._id,
          availabilityDates: futureAvailabilityDates,
        },
      },
    },
  };

export const futureAvailabilityIntervalsRequest: MockedResponse<GetOrganisationAvailabilityDatesQuery> =
  {
    request: {
      query: GetOrganisationAvailabilityDatesDocument,
      variables: {
        bookingToolId: defaultBookingTool._id,
        duration: 30,
        id: defaultOrganisation._id,
        meta: {
          sessionId: "12345678",
        },

        ranges: sixMonthRanges,
        requestRangesIndividually: true,
        serviceId: defaultService._id,
        spaces: 1,
        timezone: "Europe/London",
      },
    },
    result: {
      data: {
        organisation: {
          _id: defaultOrganisation._id,
          availabilityDates: futureAvailabilityDates,
        },
      },
    },
  };

export const noFutureAvailabilityIntervalsRequest: MockedResponse<GetOrganisationAvailabilityDatesQuery> =
  {
    request: {
      query: GetOrganisationAvailabilityDatesDocument,
      variables: {
        bookingToolId: defaultBookingTool._id,
        duration: 30,
        id: defaultOrganisation._id,
        meta: {
          sessionId: "12345678",
        },

        ranges: sixMonthRanges,
        requestRangesIndividually: true,
        serviceId: defaultService._id,
        spaces: 1,
        timezone: "Europe/London",
      },
    },
    result: {
      data: {
        organisation: {
          _id: defaultOrganisation._id,
          availabilityDates: [],
        },
      },
    },
  };
